<script setup>
import { ref, onMounted, watch, computed, watchEffect } from 'vue'

import CheckIconInput from '@/components/input/checkIconInput.vue'

import { useTableCsv } from './useTableCsv.js'

const emit = defineEmits(['update:modelValue', 'rowClick'])

const props = defineProps({
  data: {
    type: Array,
    required: true,
  },
  fields: {
    type: Array,
    required: true,
  },
  csvEnabled: {
    type: Boolean,
    default: false,
  },
  emptyMessage: {
    type: String,
    default: 'Sem dados!',
  },
  selectable: {
    type: Boolean,
    default: false,
  },
  selectSingle: {
    type: Boolean,
    default: false,
  },
  clickableRow: {
    type: Boolean,
    default: false,
  },
  csvFileName: {
    type: String,
    default: '',
  },
  rowErrors: {
    type: Object,
    required: false,
  },
})

const selectableStatusMapping = {
  empty: [{ icon: 'square', next: 'full' }],
  intermediary: [
    { icon: 'minus-square', next: 'empty' },
    { icon: 'add-square', next: 'full' },
  ],
  full: [{ icon: 'tick-square', next: 'empty' }],
}

const value = defineModel({ type: Array, default: [] })

const innerSelected = ref([])
const selectableStatus = ref('empty')
const tableRef = ref(null)

const maxLength = computed(() => {
  return props.data.length
})

const selectedLength = computed(() => {
  return innerSelected.value.length
})

const isAllSelected = computed(() => {
  return selectedLength.value >= maxLength.value
})

const hasAlmostAllSelected = computed(() => {
  return selectedLength.value > 0 && selectedLength.value < maxLength.value
})

// Computed para determinar se uma coluna deve ser sticky
const isColumnSticky = computed(() => {
  return (field, index) => {
    // Se é selecionável e é a primeira coluna
    if (props.selectable && index === 0) return true
    // Se o campo tem sticky: true
    if (field.sticky) return true
    return false
  }
})

// Computed para calcular a posição left do sticky
const getStickyLeftPosition = computed(() => {
  return (field, index) => {
    if (!isColumnSticky.value(field, index)) return undefined

    // Se é selecionável, a primeira coluna fica em left: 0
    if (props.selectable && index === 0) return '0px'

    // Se é sticky mas não é a primeira coluna selecionável
    if (field.sticky) {
      // Se tem selecionável, precisa calcular baseado na largura da coluna de seleção
      // Por simplicidade, vamos usar 80px como largura aproximada da coluna de checkbox
      return props.selectable ? '80px' : '0px'
    }

    return '0px'
  }
})

onMounted(() => {
  innerSelected.value = value.value
})

const { createCsv } = useTableCsv(tableRef, () => props.csvFileName)

const doNewStatusAction = (old_status, new_status) => {
  if (new_status === 'empty') {
    innerSelected.value = []
  } else if (new_status === 'full') {
    props.data.forEach(item => {
      if (!innerSelected.value.includes(item.id)) {
        innerSelected.value.push(item.id)
      }
    })
  }
}

const handleRowClick = item => {
  if (props.clickableRow) {
    emit('rowClick', item)
  }
}

watchEffect(() => {
  if (isAllSelected.value) {
    selectableStatus.value = 'full'
  } else if (hasAlmostAllSelected.value) {
    selectableStatus.value = 'intermediary'
  } else {
    selectableStatus.value = 'empty'
  }

  emit('update:modelValue', innerSelected.value)
})

watch(value, () => {
  innerSelected.value = value.value
})

defineExpose({
  createCsv,
})
</script>

<template>
  <div class="flex w-full flex-col overflow-x-auto">
    <table ref="tableRef" class="w-full border-collapse items-center">
      <thead
        class="bg-neutral-0 text-neutral-60 hidden border-b border-[#EAECF0] md:table-header-group"
      >
        <tr class="md:border-b md:border-[#EAECF0]">
          <th
            v-for="(field, i) in fields"
            :key="`head-${i}`"
            scope="col"
            class="text-neutral-60 h-11 px-6 py-3 text-left text-sm font-semibold whitespace-nowrap"
            :class="{
              'bg-neutral-0 sticky top-0 z-10': isColumnSticky(field, i),
            }"
            :style="{
              left: getStickyLeftPosition(field, i),
            }"
          >
            <span class="flex flex-row gap-3">
              <CheckIconInput
                v-if="selectable && i === 0"
                v-model="selectableStatus"
                :iconsStatusMapping="selectableStatusMapping"
                @statusChange="doNewStatusAction"
              />
              <slot :name="`head(${field.key})`" :item="field">
                {{ field.label }}
              </slot>
            </span>
          </th>
        </tr>
      </thead>
      <tbody v-if="data.length">
        <tr
          v-for="(item, i) in data"
          :key="`r-${i}`"
          class="my-2 border border-[#EAECF0] md:border-x-0 md:border-t-0 md:border-b"
          :class="{
            'hover:bg-neutral-20 cursor-pointer': clickableRow,
            error:
              rowErrors &&
              rowErrors?.errorsIndex.includes(
                `${(rowErrors.currentPage - 1) * Number(rowErrors.perPage) + i}`,
              ),
          }"
          :data-index="i"
          @click="handleRowClick(item)"
        >
          <td
            v-for="(field, j) in fields"
            :data-label="field.label"
            :key="`j-${j}`"
            class="flex items-end justify-between truncate p-3 px-6 leading-4 whitespace-nowrap md:table-cell md:border-none md:before:hidden"
            :class="{
              'sticky z-10 bg-white': isColumnSticky(field, j),
            }"
            :style="{
              left: getStickyLeftPosition(field, j),
            }"
          >
            <div class="text-neutral-60 text-sm font-normal">
              <div class="flex flex-row gap-3" :class="`${field.align}`">
                <input
                  v-if="selectable && j === 0"
                  v-model="innerSelected"
                  :id="`row-${item.id}`"
                  :value="item.id"
                  class="text-primary-80 focus:ring-primary-90 cursor-pointer rounded-md"
                  :type="selectSingle ? 'radio' : 'checkbox'"
                />
                <slot :name="`cell(${field.key})`" :item="item" :row="i" :column="j">
                  {{
                    field.formater && item[field.key] != null
                      ? field.formater(item[field.key])
                      : item[field.key]
                  }}
                </slot>
              </div>
            </div>
          </td>
        </tr>
      </tbody>
      <tbody v-else>
        <tr class="p-3 text-sm whitespace-nowrap">
          <td
            :colspan="selectable ? fields.length + 1 : fields.length"
            class="p-3 text-center text-lg whitespace-nowrap"
          >
            <slot name="noPageView" class="w-full">
              {{ emptyMessage }}
            </slot>
          </td>
        </tr>
      </tbody>
    </table>
    <div v-if="data.length" class="sticky top-0 left-0 border-x-2 border-white">
      <slot name="pagination" />
    </div>
    <div
      v-if="csvEnabled && data.length"
      class="bg-neutral-0 sticky top-0 left-0 flex flex-row justify-items-end rounded-b-lg border-2 border-white px-4 py-2"
    >
      <ImoButton
        @click="createCsv"
        icon="document-download"
        variant="tertiary"
        size="sm"
        text="Baixar CSV"
      />
    </div>
  </div>
</template>

<style scoped>
@reference "@/assets/style/main.css";

table {
  td {
    &:before {
      content: attr(data-label);
      text-align: left;
      color: #475569;
      font-weight: bold;
      font-size: 12px;
    }
  }
}

.error {
  @apply bg-error-10 border-error-60 text-error-60 border-none;
  box-shadow: 0px 0px 0px 3px #f008024d;
}
</style>
